import type { ListMyOrganizationCasesRequest } from '@compass/client/types/case_record';
import { caseRecordService } from '@services/api';
import { queryKeys } from '@services/query.config';
import { useStore } from '@store/index';
import { useQuery } from '@tanstack/react-query';

import { CASES_QUERY_CONFIG } from '@/constants/queryConfig';
import { paginateCursorList } from '@/utils/paginateCursorList';

export interface UseAllMyCasesParams {
  program_ids?: string[];
  case_record_ids?: string[];
  created_after?: string;
  created_before?: string;
}

interface UseAllMyCasesOptions {
  /** Max number of pages to fetch (each page = `pageSize` rows). Bounds total work
   * for views like the Pending tab that need every outstanding case but must not
   * scan the entire org. Default 5 pages × 100 = up to 500 rows. */
  maxPages?: number;
  /** Per-request limit. Capped by the backend at 100. */
  pageSize?: number;
  /** Pass false to keep the query idle (e.g. when the All tab is active). */
  enabled?: boolean;
}

/**
 * Fetches every case assigned to the current user that matches the given filters
 * by paginating through `listMyOrganizationCases` up to `maxPages` times.
 *
 * Used by My Cases for both tabs: the Pending tab filters and sorts the full set
 * client-side (since no server-side state filter exists), while the All tab
 * slices the same dataset for client-side pagination — this keeps tab counts
 * consistent (Pending is always a strict subset of All).
 */
export const useAllMyCases = (params: UseAllMyCasesParams, options: UseAllMyCasesOptions = {}) => {
  const selectedOrganizationId = useStore(state => state.selectedOrganizationId);
  const maxPages = options.maxPages ?? 5;
  const pageSize = options.pageSize ?? 100;
  const isEnabled = !!selectedOrganizationId && options.enabled !== false;

  const queryKeyParams = {
    program_ids: params.program_ids,
    case_record_ids: params.case_record_ids,
    created_after: params.created_after,
    created_before: params.created_before,
    pageSize,
    maxPages,
    all: true,
  };

  const query = useQuery({
    queryKey: queryKeys.myCases.byOrg(selectedOrganizationId || '', queryKeyParams),
    queryFn: () =>
      paginateCursorList(
        async cursor => {
          const request: ListMyOrganizationCasesRequest = {
            organization_id: selectedOrganizationId!,
            program_ids: params.program_ids,
            case_record_ids: params.case_record_ids,
            created_after: params.created_after,
            created_before: params.created_before,
            limit: pageSize,
            start_after_id: cursor,
          };
          const res = await caseRecordService.listMyOrganizationCases(request);
          return { records: res.case_records, next_cursor: res.next_cursor };
        },
        maxPages,
        pageSize,
      ),
    enabled: isEnabled,
    // Spread CASES_QUERY_CONFIG so My Cases picks up any future shared defaults
    // (gcTime, refetchOnWindowFocus, etc.), but explicitly opt OUT of the 30s
    // refetchInterval: that polling is appropriate for a single-page list query,
    // not for this hook's up-to-maxPages fan-out (which would multiply request
    // volume by maxPages every 30s). staleTime alone is enough freshness; an
    // explicit React-Query invalidation handles user-driven refreshes.
    ...CASES_QUERY_CONFIG,
    refetchInterval: false,
  });

  return {
    ...query,
    cases: query.data?.records ?? [],
    // `truncated` is true only when the pagination loop hit the maxPages cap
    // with more data still available beyond it — useful for surfacing a soft
    // warning ("showing first 500 pending cases").
    truncated: query.data?.reachedCap ?? false,
  };
};
